import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/utils/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/* ======= Floating SVG haberdashery icons ======= */
function Scissors({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="18" cy="18" r="8" stroke="currentColor" strokeWidth="2" fill="none" />
      <circle cx="18" cy="46" r="8" stroke="currentColor" strokeWidth="2" fill="none" />
      <line x1="25" y1="22" x2="52" y2="42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="25" y1="42" x2="52" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Needle({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="12" y1="52" x2="48" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="50" cy="6" r="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path d="M12 52 Q 8 56, 14 58 Q 20 54, 12 52" stroke="currentColor" strokeWidth="1.5" fill="currentColor" opacity="0.3" />
      <path d="M18 44 C 24 48 28 42 22 38" stroke="currentColor" strokeWidth="1" strokeDasharray="2 3" opacity="0.4" />
    </svg>
  );
}

function PinCushion({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="32" cy="40" rx="22" ry="14" stroke="currentColor" strokeWidth="2" fill="currentColor" opacity="0.08" />
      <ellipse cx="32" cy="38" rx="22" ry="12" stroke="currentColor" strokeWidth="2" fill="none" />
      <line x1="24" y1="38" x2="22" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="22" cy="14" r="2.5" fill="currentColor" opacity="0.4" />
      <line x1="32" y1="36" x2="34" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="34" cy="10" r="2.5" fill="currentColor" opacity="0.4" />
      <line x1="40" y1="36" x2="42" y2="18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="42" cy="16" r="2.5" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

function Thread({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="16" y="20" width="32" height="28" rx="4" stroke="currentColor" strokeWidth="2" fill="currentColor" opacity="0.06" />
      <ellipse cx="32" cy="18" rx="14" ry="6" stroke="currentColor" strokeWidth="2" fill="none" />
      <line x1="32" y1="12" x2="32" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M 20 34 Q 26 28 32 34 Q 38 40 44 34" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.5" />
    </svg>
  );
}

function MeasuringTape({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 32 Q 8 16 24 16 L 48 16 Q 56 16 56 24 L 56 32" stroke="currentColor" strokeWidth="2" fill="none" />
      <rect x="4" y="30" width="12" height="18" rx="3" stroke="currentColor" strokeWidth="2" fill="currentColor" opacity="0.06" />
      <line x1="24" y1="16" x2="24" y2="22" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      <line x1="32" y1="16" x2="32" y2="20" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      <line x1="40" y1="16" x2="40" y2="22" stroke="currentColor" strokeWidth="1" opacity="0.4" />
      <line x1="48" y1="16" x2="48" y2="20" stroke="currentColor" strokeWidth="1" opacity="0.4" />
    </svg>
  );
}

function SewingButton({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="22" stroke="currentColor" strokeWidth="2" fill="currentColor" opacity="0.04" />
      <circle cx="32" cy="32" r="16" stroke="currentColor" strokeWidth="1" opacity="0.3" />
      <circle cx="26" cy="28" r="2.5" fill="currentColor" opacity="0.35" />
      <circle cx="38" cy="28" r="2.5" fill="currentColor" opacity="0.35" />
      <circle cx="26" cy="38" r="2.5" fill="currentColor" opacity="0.35" />
      <circle cx="38" cy="38" r="2.5" fill="currentColor" opacity="0.35" />
      <line x1="26" y1="28" x2="38" y2="38" stroke="currentColor" strokeWidth="1" opacity="0.2" />
      <line x1="38" y1="28" x2="26" y2="38" stroke="currentColor" strokeWidth="1" opacity="0.2" />
    </svg>
  );
}

function Thimble({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 42 Q20 14 32 14 Q44 14 44 42" stroke="currentColor" strokeWidth="2" fill="currentColor" opacity="0.05" />
      <line x1="20" y1="42" x2="44" y2="42" stroke="currentColor" strokeWidth="2" />
      <ellipse cx="32" cy="46" rx="12" ry="4" stroke="currentColor" strokeWidth="1.5" fill="currentColor" opacity="0.04" />
      <circle cx="28" cy="22" r="1.5" fill="currentColor" opacity="0.3" />
      <circle cx="36" cy="22" r="1.5" fill="currentColor" opacity="0.3" />
      <circle cx="32" cy="18" r="1.5" fill="currentColor" opacity="0.3" />
      <circle cx="28" cy="30" r="1.5" fill="currentColor" opacity="0.3" />
      <circle cx="36" cy="30" r="1.5" fill="currentColor" opacity="0.3" />
      <circle cx="32" cy="36" r="1.5" fill="currentColor" opacity="0.3" />
    </svg>
  );
}

function FabricSwatch({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="10" y="10" width="44" height="44" rx="4" stroke="currentColor" strokeWidth="2" fill="currentColor" opacity="0.04" />
      <line x1="10" y1="22" x2="54" y2="22" stroke="currentColor" strokeWidth="0.8" opacity="0.15" />
      <line x1="10" y1="34" x2="54" y2="34" stroke="currentColor" strokeWidth="0.8" opacity="0.15" />
      <line x1="10" y1="46" x2="54" y2="46" stroke="currentColor" strokeWidth="0.8" opacity="0.15" />
      <line x1="22" y1="10" x2="22" y2="54" stroke="currentColor" strokeWidth="0.8" opacity="0.15" />
      <line x1="34" y1="10" x2="34" y2="54" stroke="currentColor" strokeWidth="0.8" opacity="0.15" />
      <line x1="46" y1="10" x2="46" y2="54" stroke="currentColor" strokeWidth="0.8" opacity="0.15" />
    </svg>
  );
}

function Zipper({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="28" y="4" width="8" height="56" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" opacity="0.04" />
      <path d="M28 16 L24 20 L28 24 L24 28 L28 32 L24 36 L28 40 L24 44 L28 48" stroke="currentColor" strokeWidth="1.2" opacity="0.3" />
      <path d="M36 16 L40 20 L36 24 L40 28 L36 32 L40 36 L36 40 L40 44 L36 48" stroke="currentColor" strokeWidth="1.2" opacity="0.3" />
      <rect x="26" y="6" width="12" height="8" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" opacity="0.08" />
    </svg>
  );
}

function DressForm({ className }) {
  return (
    <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 12 Q24 6 32 6 Q40 6 40 12 L42 36 Q42 42 32 44 Q22 42 22 36 Z" stroke="currentColor" strokeWidth="2" fill="currentColor" opacity="0.04" />
      <line x1="32" y1="44" x2="32" y2="56" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="24" y1="56" x2="40" y2="56" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="28" y1="58" x2="36" y2="58" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
    </svg>
  );
}

/* All floating items - rich, dense, layered */
const floatingItems = [
  // Large anchor pieces
  { Icon: Scissors, top: "4%", left: "4%", size: "w-20 h-20 sm:w-28 sm:h-28", anim: "animate-float", delay: "0s", opacity: "opacity-[0.14]" },
  { Icon: DressForm, top: "8%", right: "5%", size: "w-18 h-18 sm:w-24 sm:h-24", anim: "animate-float-slow", delay: "1.2s", opacity: "opacity-[0.10]" },
  { Icon: PinCushion, bottom: "10%", left: "3%", size: "w-20 h-20 sm:w-28 sm:h-28", anim: "animate-float-reverse", delay: "0.5s", opacity: "opacity-[0.12]" },
  { Icon: Thread, bottom: "8%", right: "4%", size: "w-16 h-16 sm:w-22 sm:h-22", anim: "animate-float", delay: "2s", opacity: "opacity-[0.14]" },

  // Medium secondary pieces
  { Icon: MeasuringTape, top: "45%", left: "2%", size: "w-14 h-14 sm:w-20 sm:h-20", anim: "animate-float-slow", delay: "1.5s", opacity: "opacity-[0.10]" },
  { Icon: Thimble, top: "30%", right: "3%", size: "w-12 h-12 sm:w-18 sm:h-18", anim: "animate-float-reverse", delay: "3s", opacity: "opacity-[0.11]" },
  { Icon: SewingButton, top: "55%", right: "6%", size: "w-12 h-12 sm:w-16 sm:h-16", anim: "animate-float", delay: "0.8s", opacity: "opacity-[0.10]" },
  { Icon: FabricSwatch, bottom: "30%", left: "5%", size: "w-14 h-14 sm:w-18 sm:h-18", anim: "animate-float-slow", delay: "2.5s", opacity: "opacity-[0.09]" },
  { Icon: Needle, top: "18%", left: "10%", size: "w-10 h-10 sm:w-16 sm:h-16", anim: "animate-float", delay: "1.8s", opacity: "opacity-[0.12]", rotate: "-rotate-12" },
  { Icon: Zipper, bottom: "20%", right: "8%", size: "w-10 h-10 sm:w-14 sm:h-14", anim: "animate-float-reverse", delay: "3.5s", opacity: "opacity-[0.08]" },

  // Small accent pieces
  { Icon: Scissors, bottom: "40%", right: "2%", size: "w-8 h-8 sm:w-12 sm:h-12", anim: "animate-float-reverse", delay: "4s", opacity: "opacity-[0.07]", rotate: "rotate-45" },
  { Icon: Needle, top: "65%", left: "3%", size: "w-8 h-8 sm:w-10 sm:h-10", anim: "animate-float", delay: "2.2s", opacity: "opacity-[0.06]", rotate: "rotate-12" },
  { Icon: PinCushion, top: "3%", right: "25%", size: "w-10 h-10 sm:w-14 sm:h-14", anim: "animate-float-slow", delay: "0.8s", opacity: "opacity-[0.08]" },
  { Icon: SewingButton, top: "75%", right: "10%", size: "w-8 h-8 sm:w-10 sm:h-10", anim: "animate-float", delay: "1s", opacity: "opacity-[0.07]" },
  { Icon: Thread, bottom: "45%", left: "8%", size: "w-8 h-8 sm:w-12 sm:h-12", anim: "animate-float-reverse", delay: "3.2s", opacity: "opacity-[0.06]" },
  { Icon: Thimble, top: "2%", left: "30%", size: "w-8 h-8 sm:w-10 sm:h-10", anim: "animate-float-slow", delay: "4.5s", opacity: "opacity-[0.07]" },
  { Icon: FabricSwatch, bottom: "5%", left: "30%", size: "w-10 h-10 sm:w-14 sm:h-14", anim: "animate-float", delay: "1.6s", opacity: "opacity-[0.06]" },
  { Icon: DressForm, top: "38%", right: "12%", size: "w-10 h-10 sm:w-14 sm:h-14", anim: "animate-float-reverse", delay: "2.8s", opacity: "opacity-[0.06]" },
  { Icon: MeasuringTape, bottom: "55%", right: "15%", size: "w-8 h-8 sm:w-12 sm:h-12", anim: "animate-float-slow", delay: "0.3s", opacity: "opacity-[0.05]" },
  { Icon: Zipper, top: "80%", left: "12%", size: "w-8 h-8 sm:w-10 sm:h-10", anim: "animate-float", delay: "3.8s", opacity: "opacity-[0.06]" },

  // Extra dense layer — fill the gaps
  { Icon: Scissors, top: "25%", left: "18%", size: "w-10 h-10 sm:w-16 sm:h-16", anim: "animate-drift", delay: "0.2s", opacity: "opacity-[0.09]", rotate: "-rotate-6" },
  { Icon: Thread, top: "70%", right: "18%", size: "w-12 h-12 sm:w-18 sm:h-18", anim: "animate-drift", delay: "1.4s", opacity: "opacity-[0.10]" },
  { Icon: Needle, top: "42%", left: "20%", size: "w-8 h-8 sm:w-12 sm:h-12", anim: "animate-float-diag", delay: "2.6s", opacity: "opacity-[0.07]", rotate: "rotate-45" },
  { Icon: DressForm, bottom: "15%", right: "22%", size: "w-14 h-14 sm:w-20 sm:h-20", anim: "animate-float-slow", delay: "0.6s", opacity: "opacity-[0.08]" },
  { Icon: SewingButton, top: "12%", left: "35%", size: "w-8 h-8 sm:w-12 sm:h-12", anim: "animate-drift-wide", delay: "3.4s", opacity: "opacity-[0.07]" },
  { Icon: PinCushion, top: "85%", right: "30%", size: "w-10 h-10 sm:w-16 sm:h-16", anim: "animate-float-reverse", delay: "1.2s", opacity: "opacity-[0.08]" },
  { Icon: Thimble, bottom: "60%", left: "15%", size: "w-10 h-10 sm:w-14 sm:h-14", anim: "animate-float-diag", delay: "4.2s", opacity: "opacity-[0.06]" },
  { Icon: FabricSwatch, top: "58%", right: "20%", size: "w-12 h-12 sm:w-16 sm:h-16", anim: "animate-drift", delay: "0.9s", opacity: "opacity-[0.07]" },
  { Icon: Zipper, top: "92%", left: "25%", size: "w-8 h-8 sm:w-12 sm:h-12", anim: "animate-float", delay: "2.4s", opacity: "opacity-[0.06]" },
  { Icon: MeasuringTape, top: "5%", left: "48%", size: "w-12 h-12 sm:w-16 sm:h-16", anim: "animate-drift-wide", delay: "1.8s", opacity: "opacity-[0.08]" },
  { Icon: Scissors, bottom: "3%", right: "40%", size: "w-10 h-10 sm:w-14 sm:h-14", anim: "animate-float-slow", delay: "3.6s", opacity: "opacity-[0.07]", rotate: "rotate-12" },
  { Icon: Thread, top: "33%", right: "28%", size: "w-8 h-8 sm:w-12 sm:h-12", anim: "animate-float-reverse", delay: "0.4s", opacity: "opacity-[0.06]" },
  { Icon: Needle, bottom: "38%", left: "25%", size: "w-10 h-10 sm:w-14 sm:h-14", anim: "animate-float-diag", delay: "5s", opacity: "opacity-[0.07]", rotate: "-rotate-45" },
  { Icon: SewingButton, top: "48%", left: "42%", size: "w-6 h-6 sm:w-10 sm:h-10", anim: "animate-drift", delay: "2.1s", opacity: "opacity-[0.05]" },
  { Icon: DressForm, top: "22%", right: "40%", size: "w-8 h-8 sm:w-12 sm:h-12", anim: "animate-float-slow", delay: "4.8s", opacity: "opacity-[0.06]" },
  { Icon: PinCushion, top: "62%", left: "38%", size: "w-10 h-10 sm:w-14 sm:h-14", anim: "animate-drift-wide", delay: "1.6s", opacity: "opacity-[0.06]" },
  { Icon: FabricSwatch, bottom: "22%", right: "38%", size: "w-8 h-8 sm:w-12 sm:h-12", anim: "animate-float", delay: "3.2s", opacity: "opacity-[0.05]" },
  { Icon: Thimble, top: "88%", right: "15%", size: "w-8 h-8 sm:w-10 sm:h-10", anim: "animate-float-reverse", delay: "0.7s", opacity: "opacity-[0.06]" },
  { Icon: Zipper, top: "15%", right: "35%", size: "w-10 h-10 sm:w-14 sm:h-14", anim: "animate-float-diag", delay: "2.8s", opacity: "opacity-[0.07]" },
  { Icon: MeasuringTape, bottom: "48%", right: "32%", size: "w-8 h-8 sm:w-12 sm:h-12", anim: "animate-drift", delay: "4.4s", opacity: "opacity-[0.05]" },
];

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F3EF] flex items-center justify-center p-4 sm:p-6 relative overflow-hidden">
      {/* Dense floating haberdashery field */}
      {floatingItems.map((item, i) => {
        const { Icon, size, anim, delay, opacity, rotate, ...pos } = item;
        return (
          <div
            key={`float-${i}`}
            className={`absolute text-[#5C3A2E] ${size} ${anim} ${opacity} ${rotate || ""} pointer-events-none select-none`}
            style={{ ...pos, animationDelay: delay }}
          >
            <Icon className="w-full h-full" />
          </div>
        );
      })}

      {/* Subtle radial glow behind form */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(198,169,105,0.08) 0%, transparent 70%)" }} />

      {/* Grain overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")" }} />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8 sm:mb-10 animate-fade-in-up">
          <img src="/kalemash-logo.png" alt="Kalemash" className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-5 rounded-2xl shadow-[0_8px_30px_rgb(92,58,46,0.15)] hover:scale-105 transition-transform duration-300" />
          <h1 className="font-heading text-4xl sm:text-5xl text-[#5C3A2E] font-semibold tracking-tight">
            Kalemash
          </h1>
          <p className="text-[#2B2B2B]/50 mt-2 font-body text-sm sm:text-base">Welcome back to your studio</p>
        </div>

        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-[0_8px_30px_rgb(92,58,46,0.1)] border border-[#EADBC8]/40 p-6 sm:p-8 animate-fade-in-up animate-stagger-2">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div data-testid="login-error" className="bg-red-50 text-red-700 text-sm p-3 rounded-xl border border-red-100 animate-scale-in">
                {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[#2B2B2B] mb-1.5">Email</label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full rounded-xl border-[#EADBC8] bg-white/70 px-4 py-3 text-[#2B2B2B] focus:ring-2 focus:ring-[#C6A969] focus:border-transparent placeholder:text-[#2B2B2B]/30 transition-all duration-200 hover:border-[#C6A969]/50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2B2B] mb-1.5">Password</label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full rounded-xl border-[#EADBC8] bg-white/70 px-4 py-3 text-[#2B2B2B] focus:ring-2 focus:ring-[#C6A969] focus:border-transparent placeholder:text-[#2B2B2B]/30 transition-all duration-200 hover:border-[#C6A969]/50"
                required
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#5C3A2E] text-white rounded-full py-3 hover:bg-[#5C3A2E]/90 hover:shadow-[0_8px_24px_rgb(92,58,46,0.25)] transition-all duration-300 font-medium text-base h-12 active:scale-[0.98]"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : "Sign In"}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-[#2B2B2B]/50">
            New to the studio?{" "}
            <Link to="/signup" data-testid="goto-signup-link" className="text-[#5C3A2E] font-medium hover:text-[#C6A969] transition-colors duration-200 underline-offset-2 hover:underline">
              Create an account
            </Link>
          </p>
          <p className="text-center mt-2 text-sm">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[#2B2B2B]/35 hover:text-[#C6A969] transition-colors duration-200">
              Forgot your password?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
